import { useEffect, useEffectEvent, useRef } from 'react';
import { useIsMutating } from '@tanstack/react-query';

import { useOrganization } from '@/contexts/organization-context';
import { isDesktopPlatform } from '@/lib/desktop/bridge';

import { useNotificationSound } from './use-notification-sound';
import { useUnreadNotificationCount } from './use-notifications';

/**
 * Singleton notification sound monitor.
 * Mount once at the app root (AppRoutes). Do NOT mount in feature components.
 * Use useNotificationSound() directly for on-demand playback.
 *
 * Plays a chime when the unread notification count genuinely increases due to
 * a new notification arriving — not on initialization, org switch, bulk mutations,
 * or background-tab refetches.
 *
 * Guards:
 * - `isPending` prevents sound on first load (initialization race)
 * - Combined `baselineRef` with orgId prevents sound on organization switch
 * - `isMutating` + `wasMutatingRef` prevents sound on bulk mark-as-unread (PR #974)
 * - `document.hidden` prevents sound on background tabs
 * - `useEffectEvent` prevents re-subscription on preference changes
 */
export function useNotificationSoundMonitor(): void {
  const { currentOrganizationId } = useOrganization();
  const { data: countData, isPending } = useUnreadNotificationCount();
  const playNotificationSound = useNotificationSound();
  const isMutating = useIsMutating({ mutationKey: ['notifications'] });
  const wasMutatingRef = useRef(false);
  const baselineRef = useRef<{ orgId: string | null; count: number | null }>({
    orgId: null,
    count: null,
  });

  const handleCountChange = useEffectEvent(() => {
    // Query hasn't loaded yet — don't set baseline from loading placeholder
    if (isPending) {
      return;
    }

    const currentCount = countData ?? 0;

    // Org changed — re-baseline silently, no sound
    if (baselineRef.current.orgId !== currentOrganizationId) {
      baselineRef.current = { orgId: currentOrganizationId, count: currentCount };
      return;
    }

    // First load — initialize baseline, no sound
    if (baselineRef.current.count === null) {
      baselineRef.current = { orgId: currentOrganizationId, count: currentCount };
      return;
    }

    // Skip sound when local mutations are in-flight (e.g. bulk mark-as-unread).
    if (isMutating > 0) {
      wasMutatingRef.current = true;
      return;
    }

    // Mutations just settled — sync baseline without playing sound.
    // The count delta includes the user's own action (e.g. bulk mark-as-unread),
    // so playing a sound here would be a false positive.
    if (wasMutatingRef.current) {
      wasMutatingRef.current = false;
      baselineRef.current = { orgId: currentOrganizationId, count: currentCount };
      return;
    }

    // Sound trigger — only on genuine count increase, only on visible tab
    if (
      currentCount > baselineRef.current.count &&
      typeof document !== 'undefined' &&
      !document.hidden &&
      !isDesktopPlatform()
    ) {
      playNotificationSound();
    }

    // ALWAYS update baseline, even when tab is hidden
    baselineRef.current = { orgId: currentOrganizationId, count: currentCount };
  });

  useEffect(() => {
    handleCountChange();
  }, [countData, isMutating]);
}
